/**
 * Lógica pura de las opiniones de usuarios (web + mobile, build de la home).
 * Migración: supabase/migrations/20260928130000_opiniones_usuarios.sql
 * Spec: docs/superpowers/specs/2026-09-28-opiniones-usuarios-design.md
 */

/** Mismo listado que el CHECK de la tabla `opiniones.provincia`. */
export const PROVINCIAS_AR = [
  'Buenos Aires',
  'Ciudad Autónoma de Buenos Aires',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucumán',
] as const;

export const OPINION_TEXTO_MIN = 20;
export const OPINION_TEXTO_MAX = 1000;
export const OPINION_NOMBRE_MAX = 60;
export const OPINION_ESTABLECIMIENTO_MAX = 80;

/** Días de uso antes de mostrar el aviso único pidiendo la opinión. */
export const DIAS_PARA_AVISO_OPINION = 14;

export type EstadoOpinion = 'pendiente' | 'aprobada' | 'rechazada';

export interface OpinionInput {
  calificacion: number;
  texto: string;
  nombre_publico: string;
  establecimiento: string;
  provincia: string;
  autoriza_publicar: boolean;
}

export type OpinionErrores = Partial<Record<keyof OpinionInput, string>>;

/** Mismas reglas que los CHECK de la tabla, con mensajes para el productor. */
export function validarOpinion(input: OpinionInput): OpinionErrores {
  const errores: OpinionErrores = {};
  if (!Number.isInteger(input.calificacion) || input.calificacion < 1 || input.calificacion > 5) {
    errores.calificacion = 'Elegí de 1 a 5 estrellas.';
  }
  const texto = input.texto.trim();
  if (texto.length < OPINION_TEXTO_MIN) {
    errores.texto = `Contanos un poco más (mínimo ${OPINION_TEXTO_MIN} letras).`;
  } else if (texto.length > OPINION_TEXTO_MAX) {
    errores.texto = `Máximo ${OPINION_TEXTO_MAX} letras.`;
  }
  const nombre = input.nombre_publico.trim();
  if (nombre.length < 2 || nombre.length > OPINION_NOMBRE_MAX) {
    errores.nombre_publico = 'Poné cómo querés que aparezca tu nombre.';
  }
  if (input.establecimiento.trim().length > OPINION_ESTABLECIMIENTO_MAX) {
    errores.establecimiento = `Máximo ${OPINION_ESTABLECIMIENTO_MAX} letras.`;
  }
  if (!(PROVINCIAS_AR as readonly string[]).includes(input.provincia)) {
    errores.provincia = 'Elegí tu provincia.';
  }
  if (!input.autoriza_publicar) {
    errores.autoriza_publicar = 'Necesitamos tu permiso para publicar la opinión.';
  }
  return errores;
}

/** "Juan Pérez" → "Juan P." (sugerencia editable para el nombre público). */
export function nombrePublicoSugerido(nombre?: string | null, apellido?: string | null): string {
  const n = (nombre ?? '').trim();
  const a = (apellido ?? '').trim();
  if (!n) return '';
  return a ? `${n} ${a.charAt(0).toUpperCase()}.` : n;
}

export interface ResumenOpiniones {
  cantidad: number;
  /** Promedio redondeado a 1 decimal (0 si no hay opiniones). */
  promedio: number;
}

/** Promedio y cantidad sobre TODAS las aprobadas (nunca un subconjunto). */
export function resumenOpiniones(opiniones: { calificacion: number }[]): ResumenOpiniones {
  if (opiniones.length === 0) return { cantidad: 0, promedio: 0 };
  const suma = opiniones.reduce((s, o) => s + o.calificacion, 0);
  return { cantidad: opiniones.length, promedio: Math.round((suma / opiniones.length) * 10) / 10 };
}

interface AvisoOpinionInput {
  /** Fecha de alta del usuario (auth.users.created_at). */
  usuarioCreadoEl?: string | null;
  tieneOpinion: boolean;
  avisoDescartado: boolean;
  ahora?: Date;
}

/** Aviso único: después de 14 días de uso, si no opinó ni lo cerró antes. */
export function debeMostrarAvisoOpinion({
  usuarioCreadoEl,
  tieneOpinion,
  avisoDescartado,
  ahora = new Date(),
}: AvisoOpinionInput): boolean {
  if (tieneOpinion || avisoDescartado || !usuarioCreadoEl) return false;
  const creado = new Date(usuarioCreadoEl).getTime();
  if (Number.isNaN(creado)) return false;
  const dias = (ahora.getTime() - creado) / (24 * 60 * 60 * 1000);
  return dias >= DIAS_PARA_AVISO_OPINION;
}

export const avisoOpinionStorageKey = (userId: string) => `mcd:opinion_aviso_cerrado:${userId}`;

export const ESTADO_OPINION_LABEL: Record<EstadoOpinion, string> = {
  pendiente: 'En revisión',
  aprobada: 'Publicada',
  rechazada: 'No publicada',
};

/** Opinión aprobada tal como la publica `opiniones_publicas()` (snapshot del build). */
export interface OpinionPublica {
  id: string;
  calificacion: number;
  texto: string;
  nombre_publico: string;
  establecimiento: string | null;
  provincia: string;
  publicada_el: string | null;
}

export interface OpinionesSnapshot {
  generatedAt: string;
  opiniones: OpinionPublica[];
}

/**
 * Cuántas opiniones se muestran en la home (las más recientes). El promedio y
 * la cantidad se calculan sobre TODAS las aprobadas. scripts/prerender-seo.mjs
 * usa el mismo número: lo que lee Google tiene que coincidir con lo visible.
 */
export const OPINIONES_EN_HOME = 6;

/** "4.6" → "4,6" (formato fijo es-AR, igual en navegador y prerender). */
export const formatearPromedio = (promedio: number): string => promedio.toFixed(1).replace('.', ',');
